import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const InteractiveTools = ({ onToolComplete }) => {
  const [activeTab, setActiveTab] = useState('readiness');
  const [readinessData, setReadinessData] = useState({
    currentSolution: '',
    monthlySpend: '',
    teamSize: '',
    dataVolume: ''
  });
  const [riskData, setRiskData] = useState({
    vendor: '',
    contractLength: '',
    customization: '',
    dataPortability: ''
  });

  const tabs = [
    {
      id: 'readiness',
      title: 'AI Readiness Diagnostic',
      icon: 'Activity',
      description: 'Assess your organization\'s readiness for AI transformation'
    },
    {
      id: 'risk',
      title: 'Vendor Lock-in Risk Calculator',
      icon: 'AlertTriangle',
      description: 'Calculate your current vendor dependency risk score'
    }
  ];

  const currentSolutions = [
    { value: 'none', label: 'No current AI solution' },
    { value: 'openai', label: 'OpenAI API' },
    { value: 'azure', label: 'Azure AI Services' },
    { value: 'aws', label: 'AWS AI/ML Services' },
    { value: 'google', label: 'Google Cloud AI' },
    { value: 'custom', label: 'Custom/On-premise solution' },
    { value: 'multiple', label: 'Multiple vendors' }
  ];

  const monthlySpends = [
    { value: 'under-1k', label: 'Under $1,000' },
    { value: '1k-5k', label: '$1,000 - $5,000' },
    { value: '5k-25k', label: '$5,000 - $25,000' },
    { value: '25k-100k', label: '$25,000 - $100,000' },
    { value: 'over-100k', label: 'Over $100,000' }
  ];

  const teamSizes = [
    { value: 'small', label: '1-5 people' },
    { value: 'medium', label: '6-20 people' },
    { value: 'large', label: '21-50 people' },
    { value: 'enterprise', label: '50+ people' }
  ];

  const dataVolumes = [
    { value: 'low', label: 'Low (< 1TB)' },
    { value: 'medium', label: 'Medium (1-10TB)' },
    { value: 'high', label: 'High (10-100TB)' },
    { value: 'enterprise', label: 'Enterprise (100TB+)' }
  ];

  const vendors = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'microsoft', label: 'Microsoft' },
    { value: 'google', label: 'Google' },
    { value: 'amazon', label: 'Amazon' },
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'other', label: 'Other proprietary vendor' }
  ];

  const contractLengths = [
    { value: 'monthly', label: 'Monthly subscription' },
    { value: '1-year', label: '1 year contract' },
    { value: '2-3-years', label: '2-3 years contract' },
    { value: 'over-3-years', label: 'Over 3 years contract' }
  ];

  const customizationLevels = [
    { value: 'none', label: 'No customization possible' },
    { value: 'limited', label: 'Limited configuration options' },
    { value: 'moderate', label: 'Moderate customization available' },
    { value: 'full', label: 'Full customization control' }
  ];

  const dataPortabilityLevels = [
    { value: 'none', label: 'No data export options' },
    { value: 'limited', label: 'Limited export formats' },
    { value: 'standard', label: 'Standard export formats' },
    { value: 'full', label: 'Full data portability' }
  ];

  const calculateReadinessScore = () => {
    let score = 0;
    
    // Current solution scoring
    const solutionScores = {
      'none': 20,
      'custom': 80,
      'multiple': 60,
      'openai': 40,
      'azure': 30,
      'aws': 30,
      'google': 30
    };
    score += solutionScores?.[readinessData?.currentSolution] || 0;

    // Monthly spend scoring (higher spend = more ready for change)
    const spendScores = {
      'under-1k': 20,
      '1k-5k': 40,
      '5k-25k': 60,
      '25k-100k': 80,
      'over-100k': 100
    };
    score += spendScores?.[readinessData?.monthlySpend] || 0;

    // Team size scoring
    const teamScores = {
      'small': 40,
      'medium': 60,
      'large': 80,
      'enterprise': 100
    };
    score += teamScores?.[readinessData?.teamSize] || 0;

    // Data volume scoring
    const dataScores = {
      'low': 40,
      'medium': 60,
      'high': 80,
      'enterprise': 100
    };
    score += dataScores?.[readinessData?.dataVolume] || 0;

    return Math.round(score / 4);
  };

  const calculateRiskScore = () => {
    let riskScore = 0;

    // Vendor risk scoring
    const vendorRisks = {
      'openai': 80,
      'microsoft': 70,
      'google': 70,
      'amazon': 60,
      'salesforce': 85,
      'other': 75
    };
    riskScore += vendorRisks?.[riskData?.vendor] || 0;

    // Contract length risk
    const contractRisks = {
      'monthly': 20,
      '1-year': 40,
      '2-3-years': 70,
      'over-3-years': 90
    };
    riskScore += contractRisks?.[riskData?.contractLength] || 0;

    // Customization risk (inverse - less customization = higher risk)
    const customizationRisks = {
      'none': 90,
      'limited': 70,
      'moderate': 40,
      'full': 10
    };
    riskScore += customizationRisks?.[riskData?.customization] || 0;

    // Data portability risk (inverse)
    const portabilityRisks = {
      'none': 90,
      'limited': 70,
      'standard': 40,
      'full': 10
    };
    riskScore += portabilityRisks?.[riskData?.dataPortability] || 0;

    return Math.round(riskScore / 4);
  };

  const handleReadinessSubmit = () => {
    let score = calculateReadinessScore();
    onToolComplete({
      type: 'readiness',
      score,
      data: readinessData,
      recommendation: getReadinessRecommendation(score)
    });
  };

  const handleRiskSubmit = () => {
    let score = calculateRiskScore();
    onToolComplete({
      type: 'risk',
      score,
      data: riskData,
      recommendation: getRiskRecommendation(score)
    });
  };

  const getReadinessRecommendation = (score) => {
    if (score >= 80) return 'High readiness - Perfect candidate for immediate AI transformation';
    if (score >= 60) return 'Good readiness - Ready for structured AI implementation';
    if (score >= 40) return 'Moderate readiness - Needs preparation before transformation';
    return 'Low readiness - Requires foundational work before AI adoption';
  };

  const getRiskRecommendation = (score) => {
    if (score >= 80) return 'Critical risk - Immediate action needed to reduce vendor dependency';
    if (score >= 60) return 'High risk - Should prioritize vendor independence strategy';
    if (score >= 40) return 'Moderate risk - Consider diversification options';
    return 'Low risk - Current setup provides good flexibility';
  };

  const isReadinessComplete = () => {
    return readinessData?.currentSolution && readinessData?.monthlySpend && 
           readinessData?.teamSize && readinessData?.dataVolume;
  };

  const isRiskComplete = () => {
    return riskData?.vendor && riskData?.contractLength && 
           riskData?.customization && riskData?.dataPortability;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            Interactive Assessment Tools
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Get instant insights into your AI readiness and vendor dependency risks with our interactive diagnostic tools
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row bg-gray-100 rounded-2xl p-2 mb-8">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center space-x-3 px-6 py-4 rounded-xl transition-all duration-200 ${
                activeTab === tab?.id
                  ? 'bg-white shadow-md text-brand-primary'
                  : 'text-text-secondary hover:text-brand-primary'
              }`}
            >
              <Icon name={tab?.icon} size={20} />
              <div className="text-left">
                <div className="font-semibold">{tab?.title}</div>
                <div className="text-xs opacity-75">{tab?.description}</div>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-executive p-8">
          {activeTab === 'readiness' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-brand-primary mb-2">
                  AI Readiness Diagnostic
                </h3>
                <p className="text-text-secondary">
                  Evaluate your organization's current state and readiness for AI transformation
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Current AI Solution"
                  options={currentSolutions}
                  value={readinessData?.currentSolution}
                  onChange={(value) => setReadinessData(prev => ({ ...prev, currentSolution: value }))}
                  placeholder="Select current solution"
                />

                <Select
                  label="Monthly AI Spend"
                  options={monthlySpends}
                  value={readinessData?.monthlySpend}
                  onChange={(value) => setReadinessData(prev => ({ ...prev, monthlySpend: value }))}
                  placeholder="Select monthly spend"
                />

                <Select
                  label="AI Team Size"
                  options={teamSizes}
                  value={readinessData?.teamSize}
                  onChange={(value) => setReadinessData(prev => ({ ...prev, teamSize: value }))}
                  placeholder="Select team size"
                />

                <Select
                  label="Data Volume"
                  options={dataVolumes}
                  value={readinessData?.dataVolume}
                  onChange={(value) => setReadinessData(prev => ({ ...prev, dataVolume: value }))}
                  placeholder="Select data volume"
                />
              </div>

              <div className="text-center pt-6">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleReadinessSubmit}
                  disabled={!isReadinessComplete()}
                  className="bg-brand-primary px-8"
                  iconName="BarChart"
                  iconPosition="left"
                >
                  Calculate Readiness Score
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'risk' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-brand-primary mb-2">
                  Vendor Lock-in Risk Calculator
                </h3>
                <p className="text-text-secondary">
                  Assess your current vendor dependency and associated business risks
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Select
                  label="Primary AI Vendor"
                  options={vendors}
                  value={riskData?.vendor}
                  onChange={(value) => setRiskData(prev => ({ ...prev, vendor: value }))}
                  placeholder="Select primary vendor"
                />

                <Select
                  label="Contract Length"
                  options={contractLengths}
                  value={riskData?.contractLength}
                  onChange={(value) => setRiskData(prev => ({ ...prev, contractLength: value }))}
                  placeholder="Select contract length"
                />

                <Select
                  label="Customization Level"
                  options={customizationLevels}
                  value={riskData?.customization}
                  onChange={(value) => setRiskData(prev => ({ ...prev, customization: value }))}
                  placeholder="Select customization level"
                />

                <Select
                  label="Data Portability"
                  options={dataPortabilityLevels}
                  value={riskData?.dataPortability}
                  onChange={(value) => setRiskData(prev => ({ ...prev, dataPortability: value }))}
                  placeholder="Select data portability"
                />
              </div>

              <div className="text-center pt-6">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleRiskSubmit}
                  disabled={!isRiskComplete()}
                  className="bg-red-600 hover:bg-red-700"
                  iconName="AlertTriangle"
                  iconPosition="left"
                >
                  Calculate Risk Score
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Benefits */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Zap" size={24} className="text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold text-brand-primary mb-2">Instant Results</h4>
            <p className="text-text-secondary">Get immediate scoring and recommendations based on your inputs</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Target" size={24} className="text-green-600" />
            </div>
            <h4 className="text-lg font-semibold text-brand-primary mb-2">Personalized Insights</h4>
            <p className="text-text-secondary">Receive custom recommendations tailored to your specific situation</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="FileText" size={24} className="text-purple-600" />
            </div>
            <h4 className="text-lg font-semibold text-brand-primary mb-2">Detailed Report</h4>
            <p className="text-text-secondary">Download a comprehensive report with actionable next steps</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveTools;